/** @module @liquidefy/code-editor/themes/themes @version 3.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  ThemesState,
  ThemeDefinition,
  ThemeId,
  ThemeType,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
} from '../code-editor.js';

// ═══ §2 STATE FACTORY ═══

/**
 * Returns an empty initial ThemesState.
 */
export function createThemesState(): ThemesState {
  return {
    themes: [],
    activeThemeId: undefined,
    cssVariables: {},
  };
}

// ═══ §3 REGISTRY OPERATIONS ═══

/**
 * Registers a new theme. Rejects if a theme with the same id already exists.
 */
export function registerTheme(
  state: ThemesState,
  theme: ThemeDefinition,
): EditorResult<ThemesState> {
  if (state.themes.some((t) => t.id === theme.id)) {
    return err('THEME_ALREADY_EXISTS', `Theme '${theme.id}' is already registered`);
  }
  return ok(
    { ...state, themes: [...state.themes, theme] },
    'THEME_REGISTERED',
    `Registered theme '${theme.name}'`,
  );
}

/**
 * Removes a theme by id. Refuses to remove the active theme.
 */
export function unregisterTheme(
  state: ThemesState,
  id: ThemeId,
): EditorResult<ThemesState> {
  if (state.activeThemeId === id) {
    return err('CANNOT_REMOVE_ACTIVE_THEME', `Theme '${id}' is currently active and cannot be removed`);
  }
  const idx = state.themes.findIndex((t) => t.id === id);
  if (idx === -1) {
    return err('THEME_NOT_FOUND', `No theme with id '${id}'`);
  }
  const themes = [
    ...state.themes.slice(0, idx),
    ...state.themes.slice(idx + 1),
  ];
  return ok(
    { ...state, themes },
    'THEME_UNREGISTERED',
    `Unregistered theme '${id}'`,
  );
}

/**
 * Sets the active theme and regenerates cssVariables.
 */
export function activateTheme(
  state: ThemesState,
  id: ThemeId,
): EditorResult<ThemesState> {
  const theme = state.themes.find((t) => t.id === id);
  if (!theme) {
    return err('THEME_NOT_FOUND', `No theme with id '${id}'`);
  }
  const cssVariables = generateCSSVariablesFromTheme(theme);
  return ok(
    { ...state, activeThemeId: id, cssVariables },
    'THEME_ACTIVATED',
    `Activated theme '${theme.name}'`,
  );
}

// ═══ §4 THEME QUERIES ═══

/**
 * Returns the currently active ThemeDefinition, or undefined.
 */
export function getActiveTheme(state: ThemesState): ThemeDefinition | undefined {
  if (state.activeThemeId === undefined) return undefined;
  return state.themes.find((t) => t.id === state.activeThemeId);
}

/**
 * Returns the theme with the given id, or undefined.
 */
export function getTheme(state: ThemesState, id: ThemeId): ThemeDefinition | undefined {
  return state.themes.find((t) => t.id === id);
}

/**
 * Returns all registered themes.
 */
export function listThemes(state: ThemesState): ReadonlyArray<ThemeDefinition> {
  return state.themes;
}

/**
 * Returns all themes of the given type.
 */
export function listThemesByType(
  state: ThemesState,
  type: ThemeType,
): ReadonlyArray<ThemeDefinition> {
  return state.themes.filter((t) => t.type === type);
}

/**
 * Returns true if the theme type is light.
 */
export function isLightTheme(theme: ThemeDefinition): boolean {
  return theme.type === 'light' || theme.type === 'high-contrast-light';
}

/**
 * Returns true if the theme type is dark.
 */
export function isDarkTheme(theme: ThemeDefinition): boolean {
  return theme.type === 'dark' || theme.type === 'high-contrast-dark';
}

// ═══ §5 COLOR RESOLUTION ═══

/**
 * Returns the raw color string for a theme color key, or undefined if absent.
 */
export function getColor(theme: ThemeDefinition, key: string): string | undefined {
  return theme.colors[key];
}

/**
 * Returns the color for a key, or the provided fallback if absent.
 */
export function resolveColor(theme: ThemeDefinition, key: string, fallback: string): string {
  return theme.colors[key] ?? fallback;
}

// ═══ INTERNAL ═══

/**
 * Converts theme colors to a CSS variable record.
 * 'editor.background' → '--editor-background'
 */
function generateCSSVariablesFromTheme(theme: ThemeDefinition): Readonly<Record<string, string>> {
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(theme.colors)) {
    const varName = '--' + key.replace(/\./g, '-').replace(/([A-Z])/g, '-$1').toLowerCase();
    vars[varName] = value;
  }
  return vars;
}
